import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {
    FiArrowLeft,
    FiCheckCircle,
    FiClipboard,
    FiFileText,
    FiLayers,
    FiPlus,
    FiRefreshCw,
    FiZap,
} from 'react-icons/fi';
import {BsGraphUp} from 'react-icons/bs';
import './Dashboard.css';
import {getErrorMessage, resourcesApi} from '../services/api';

const emptyStats = {documents: 0, processed: 0};

const Dashboard = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState(emptyStats);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    const loadDashboard = useCallback(async () => {
        setIsLoading(true);
        setError('');
        try {
            const documents = await resourcesApi.list();
            setStats({
                documents: documents.length,
                processed: documents.filter((item) => item.ingest_status === 'ingested').length,
            });
        } catch (requestError) {
            setError(getErrorMessage(requestError));
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    const progress = useMemo(() => {
        if (!stats.documents) return 0;
        return Math.round((stats.processed / stats.documents) * 100);
    }, [stats.documents, stats.processed]);

    const cards = [
        {icon: <FiFileText/>, title: 'منابع آموزشی', value: stats.documents, caption: 'فایل‌های بارگذاری‌شده', color: 'success'},
        {icon: <FiCheckCircle/>, title: 'اسناد پردازش‌شده', value: stats.processed, caption: 'آماده استفاده در سامانه', color: 'warning'},
        {icon: <FiLayers/>, title: 'در انتظار پردازش', value: Math.max(0, stats.documents - stats.processed), caption: 'منابع نیازمند پردازش', color: 'danger'},
    ];

    const actions = [
        {icon: <FiPlus/>, title: 'افزودن منبع', caption: 'فایل آموزشی جدید بارگذاری کنید', to: '/manageresources'},
        {icon: <FiClipboard/>, title: 'بانک سؤالات', caption: 'سؤالات تولیدشده را مرور کنید', to: '/questionBank'},
    ];

    return (
        <div className="dashboard-page">
            <header className="dashboard-topbar app-page-header app-page-header--topbar">
                <div>
                    <span className="dashboard-eyebrow">نمای کلی سامانه</span>
                    <h1><BsGraphUp/> داشبورد آموزشی</h1>
                </div>
                <button className="dashboard-refresh" type="button" onClick={loadDashboard} disabled={isLoading}>
                    <FiRefreshCw className={isLoading ? 'spin' : ''}/>
                    <span>{isLoading ? 'در حال بروزرسانی' : 'بروزرسانی'}</span>
                </button>
            </header>

            <main className="dashboard-content">
                {error && <div className="api-message error">{error}</div>}

                <section className="dashboard-hero">
                    <div className="hero-copy">
                        <span className="hero-badge"><FiZap/> دستیار آموزشی هوشمند</span>
                        <h2>مسیر ساخت آزمون را از همین‌جا شروع کنید</h2>
                        <p>منابع آموزشی را آماده کنید و در چند گام به بانک سؤال قابل استفاده برسید.</p>
                        <div className="hero-actions">
                            <button className="hero-primary-btn" onClick={() => navigate('/manageresources')}>
                                <FiPlus/> افزودن منبع جدید
                            </button>
                        </div>
                    </div>
                    <div className="hero-progress">
                        <div className="progress-ring" style={{'--progress': `${progress * 3.6}deg`}}>
                            <div><strong>{progress}%</strong><span>آمادگی منابع</span></div>
                        </div>
                        <p>{stats.processed} منبع از {stats.documents} منبع پردازش شده است.</p>
                    </div>
                </section>

                <section className="stats-grid" aria-label="آمار سامانه">
                    {cards.map((stat) => (
                        <article key={stat.title} className={`stat-card ${stat.color}`}>
                            <div className="stat-icon">{stat.icon}</div>
                            <div className="stat-info">
                                <h3>{isLoading ? '...' : stat.value}</h3>
                                <strong>{stat.title}</strong>
                                <p>{stat.caption}</p>
                            </div>
                        </article>
                    ))}
                </section>

                <div className="dashboard-panels">
                    <section className="dashboard-panel quick-actions-section">
                        <div className="section-header">
                            <div><span>میانبرها</span><h3>دسترسی سریع</h3></div>
                            <FiZap/>
                        </div>
                        <div className="actions-grid">
                            {actions.map((action) => (
                                <button key={action.to} className="action-btn" onClick={() => navigate(action.to)}>
                                    <span className="action-icon">{action.icon}</span>
                                    <span className="action-content"><strong>{action.title}</strong><small>{action.caption}</small></span>
                                    <FiArrowLeft className="action-arrow"/>
                                </button>
                            ))}
                        </div>
                    </section>

                    <aside className="dashboard-panel guide-panel">
                        <div className="section-header">
                            <div><span>راهنمای شروع</span><h3>سه گام تا آزمون</h3></div>
                            <FiCheckCircle/>
                        </div>
                        <ol className="guide-list">
                            <li><span>۱</span><div><strong>منبع را اضافه کنید</strong><small>فایل آموزشی موردنظر را بارگذاری کنید.</small></div></li>
                            <li><span>۲</span><div><strong>محتوا را پردازش کنید</strong><small>پردازش منبع و ساخت گراف را از صفحه منابع انجام دهید.</small></div></li>
                            <li><span>۳</span><div><strong>سؤال تولید کنید</strong><small>سؤالات پیشنهادی را در بانک سؤال ببینید.</small></div></li>
                        </ol>
                    </aside>
                </div>
            </main>
        </div>
    );
};

export default Dashboard;
